<template>
  <div class="date-info-header-container">
    <div class="date-and-progress-container">
      <BaseButtonIcon
        size="large"
        :title="$t('buttons.BACK_BUTTON_TITLE')"
        :aria-label="$t('buttons.BACK_BUTTON_TITLE')"
        @click="$emit('onBack')"
      >
        <ArrowIcon />
      </BaseButtonIcon>

      <div class="attendees-progress">
        <AttensiProgressBar
          :value="attendeesPercentage"
          type="radial"
          :color="progressBarColor"
          :width="20"
        >
          <div
            class="attendees-progress-text"
            data-cy="date-attendees-progress-fraction"
          >
            {{ attendeesFraction }}
          </div>
        </AttensiProgressBar>
      </div>

      <div class="date-text-info">
        <div class="start-date">
          {{ formatDateTime(date.start_datetime) }}
        </div>
        <div class="location-and-instructor">
          <span :title="date.location">{{ truncate(date.location) }}</span> |
          <span :title="date.instructor">{{ truncate(date.instructor) }}</span>
        </div>
      </div>

      <div class="date-info-header-container__actions">
        <BaseButtonIcon
          v-if="hasCoursesWritePermission && eventStatus !== 'COMPLETED'"
          outline
          :title="$t('product.EDIT_EVENT')"
          :aria-label="$t('product.EDIT_EVENT')"
          size="small"
          @click="$emit('onEdit')"
        >
          <PencilIcon />
        </BaseButtonIcon>
      </div>
    </div>

    <div class="date-actions" tabindex="0">
      <EventDateStatusChip
        size="small"
        class="status-chip"
        :status="eventStatus"
      />

      <DateAddToCalendarButton :date="date" />

      <AttendeesExport
        class="table-heading__end"
        :course-id="Number(date.id)"
        :statuses="statuses"
      />

      <BaseButtonIcon
        v-if="
          date.state !== 'expired' &&
          attendeesCount === 0 &&
          hasCoursesWritePermission
        "
        outline
        :title="$t('events.DELETE')"
        :aria-label="$t('events.DELETE')"
        size="small"
        @click="openDeleteModal"
      >
        <TrashIcon />
      </BaseButtonIcon>

      <BaseButtonIcon
        v-if="
          !isDateCancelled && attendeesCount > 0 && hasCoursesWritePermission
        "
        outline
        :title="$t('product_events.CANCEL_DATE')"
        :aria-label="$t('product_events.CANCEL_DATE')"
        size="small"
        @click="openCancelModal"
      >
        <CancelIcon />
      </BaseButtonIcon>

      <DialogModal
        v-if="isCancelModalOpen"
        :title="$t('product_events.CANCEL_DATE')"
        :description="$t('product_events.CANCEL_CONFIRM_TITLE')"
        :cta-button-text="$t('product_events.CANCEL_DATE')"
        :cancel-button-text="$t('BACK')"
        cta-button-theme="red"
        @onClose="closeCancelModal"
        @onConfirm="onCloseCancelDialog(() => $emit('onCancel'))"
      />

      <DialogModal
        v-if="isDeleteModalOpen"
        :title="$t('product_events.DELETE_DATE')"
        :description="$t('product_events.DELETE_CONFIRM_TITLE')"
        :cta-button-text="$t('product_events.DELETE_DATE')"
        :cancel-button-text="$t('BACK')"
        cta-button-theme="red"
        @onClose="closeDeleteModal"
        @onConfirm="onCloseDeleteDialog(() => $emit('onDelete'))"
      />
    </div>
  </div>
</template>

<script>
import { editPenOutlineIcon } from '@/assets/img'
import { formatNumberDigits } from '@/helpers/numbers.js'
import { AttensiProgressBar } from '@attensi/materia'
import { computed, toRefs } from 'vue'
import { truncate } from '@/helpers/filters'
import moment from 'moment'
import DateAddToCalendarButton from './DateAddToCalendarButton.vue'
import DialogModal from '@/components/generics/modals/DialogModal.vue'
import BaseButtonIcon from '@/components/generics/base/BaseButtonIcon.vue'
import TrashIcon from '@/components/icon_components/TrashIcon.vue'
import PencilIcon from '@/components/icon_components/PencilIcon.vue'
import AttendeesExport from '@/components/product/product_components/component_events/date_info_components/AttendeesExport.vue'
import CancelIcon from '@/components/icon_components/CancelIcon.vue'
import { useDialog } from '@/hooks/useDialog.js'
import ArrowIcon from '@/components/icon_components/ArrowIcon.vue'
import EventDateStatusChip from '@/components/product/product_components/component_events/date_info_components/EventDateStatusChip.vue'
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat.js'
import { ROUTE_EVENT_DATE_FORM } from '@/router/routes/routeNames.js'
import { useUserPermission } from '@/hooks/usePermission.ts'

export default {
  name: 'EvenDateInfoHeader',
  components: {
    EventDateStatusChip,
    ArrowIcon,
    CancelIcon,
    AttendeesExport,
    PencilIcon,
    TrashIcon,
    BaseButtonIcon,
    DialogModal,
    AttensiProgressBar,
    DateAddToCalendarButton,
  },
  props: {
    date: {
      type: Object,
      required: true,
    },
    statuses: {
      type: Array,
      default: undefined,
    },
  },
  emits: ['onEdit', 'onDelete', 'onCancel', 'onBack'],
  setup(props) {
    const { formatDateTime } = useDateTimeFormat()
    const { date } = toRefs(props)
    const attendeesCount = computed(() => date.value?.attendees_count || 0)
    const { canWrite: hasCoursesWritePermission } = useUserPermission('courses')

    const {
      isOpen: isDeleteModalOpen,
      openDialog: openDeleteModal,
      closeDialog: closeDeleteModal,
      onCloseDialog: onCloseDeleteDialog,
    } = useDialog()

    const {
      isOpen: isCancelModalOpen,
      openDialog: openCancelModal,
      closeDialog: closeCancelModal,
      onCloseDialog: onCloseCancelDialog,
    } = useDialog()
    return {
      hasCoursesWritePermission,
      ROUTE_EVENT_DATE_FORM,
      attendeesCount,
      editPenOutlineIcon,
      isDeleteModalOpen,
      openDeleteModal,
      closeDeleteModal,
      isCancelModalOpen,
      openCancelModal,
      closeCancelModal,
      onCloseDeleteDialog,
      onCloseCancelDialog,
      formatDateTime,
    }
  },
  computed: {
    eventStatus() {
      if (this.date.state === 'canceled') return 'CANCELLED'
      if (moment().isAfter(this.date.end_datetime)) return 'COMPLETED'
      return 'UPCOMING'
    },
    attendeesFraction() {
      const maxCount = formatNumberDigits(
        this.date.attendees_count_max || 0,
        this.$i18n.locale,
        6
      )
      return `${this.attendeesCount} / ${maxCount}`
    },
    attendeesPercentage() {
      const percent =
        (this.attendeesCount / this.date.attendees_count_max || 0) * 100

      return percent > 100 ? 100 : percent
    },
    isDateCancelled() {
      return this.date.state === 'canceled'
    },
    progressBarColor() {
      return this.isDateCancelled ? '#9b9b9b' : '#0076FF'
    },
  },
  methods: {
    truncate,
  },
}
</script>

<style lang="scss" scoped>
.date-info-header-container {
  display: flex;
  justify-content: space-between;

  &__actions {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    width: 100%;
    align-self: center;
  }

  .date-and-progress-container {
    align-items: center;
    display: flex;
    justify-content: space-between;
    gap: $spacing-xs;

    .attendees-progress {
      width: 64px;

      .attendees-progress-text {
        font-size: $font-size-xs;
        font-weight: $font-weight-black;
        line-height: $font-size-m;
        text-align: center;
        word-spacing: -1px;
      }
    }

    .date-text-info {
      .start-date {
        @include subtitle;
        text-transform: capitalize;
        color: $color-text;
      }
      .location-and-instructor {
        color: $color-text-light;
        font-size: $font-size-m;
      }
    }
  }

  .date-actions {
    display: flex;
    align-items: flex-start;
    gap: $spacing-4xs;

    .status-chip {
      margin-right: $spacing-s;
    }
  }

  .date-edit-button {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    .text-button {
      @include subtitle-2;
      color: $color-navigation;
    }
    .circle-button-icon {
      height: $spacing-s;
      width: $spacing-s;
    }
  }
}
</style>
